import { parseCouponCsvDate, validateAndBuildCouponDraft } from '@/utils/couponDraft'

export const CSV_MAX_BYTES = 5 * 1024 * 1024
export const CSV_MAX_COUPONS = 1000

export const COUPON_CSV_HEADERS = [
  'Nombre',
  'Tipo',
  'DescuentoCompra',
  'DescuentoEnvio',
  'CanjesPorUsuario',
  'CanjesTotales',
  'FechaInicio',
  'FechaFin',
  'MinimoCompra',
  'Sucursal',
]

export const COUPON_CSV_TEMPLATE = [
  COUPON_CSV_HEADERS.join(','),
  "VERANO50,carrito,50,0,1,100,'01/10/2026 00:00,'31/10/2026 23:59,500,0",
  "ENVIOGRATIS,envio,0,100,1,50,'01/10/2026 08:00,'15/10/2026 22:00,300,7127",
].join('\n')

/**
 * El apóstrofo inicial es solo la marca de texto de la plantilla en FechaInicio
 * y FechaFin. No forma parte de la fecha y no se aplica a otras columnas.
 * @param {string} value
 * @returns {string}
 */
const stripLeadingTextApostrophe = value => {
  if (typeof value !== 'string' || !value.startsWith("'")) return value
  return value.slice(1)
}

const emptyResult = (overrides = {}) => ({
  ok: false,
  delimiter: ',',
  dataRows: 0,
  emptyRows: 0,
  validRows: 0,
  invalidRows: 0,
  couponsToCreate: 0,
  errors: [],
  duplicates: [],
  existingNames: [],
  candidates: [],
  drafts: [],
  ...overrides,
})

/**
 * @param {string} text
 * @param {{ storeDivisions?: string[], byteLength?: number }} [options]
 */
export const parseAndValidateCouponCsv = (text, options = {}) => {
  const sourceText = typeof text === 'string' ? text : ''
  const byteLength = options.byteLength ?? new TextEncoder().encode(sourceText).length

  if (byteLength > CSV_MAX_BYTES) {
    return emptyResult({
      errors: [
        {
          row: null,
          message: 'El archivo CSV excede el tamaño máximo permitido (5 MB)',
        },
      ],
    })
  }

  const source = stripBom(sourceText)
  if (!source.trim()) {
    return emptyResult({
      errors: [{ row: null, message: 'El archivo CSV está vacío' }],
    })
  }

  const delimiter = detectDelimiter(source)
  const parsed = parseCsvRecords(source, delimiter)
  if (parsed.error) {
    return emptyResult({
      delimiter,
      errors: [{ row: null, message: parsed.error }],
    })
  }

  let rows = parsed.rows
  if (rows.length > 0 && isBlankRow(rows[rows.length - 1]) && /[\r\n]$/.test(source)) {
    rows = rows.slice(0, -1)
  }

  const header = rows[0]
  if (!header || !headersMatch(header)) {
    return emptyResult({
      delimiter,
      errors: [
        {
          row: 1,
          message: `El encabezado no coincide con la plantilla. Se esperaba: ${COUPON_CSV_HEADERS.join(',')}`,
        },
      ],
    })
  }

  const errors = []
  const candidates = []
  const drafts = []
  const namesByRow = []
  let emptyRows = 0
  let validRows = 0
  let invalidRows = 0

  for (let index = 1; index < rows.length; index += 1) {
    const rowNumber = index + 1
    const row = rows[index]
    if (isBlankRow(row)) {
      emptyRows += 1
      continue
    }

    if (row.length !== COUPON_CSV_HEADERS.length) {
      invalidRows += 1
      errors.push({
        row: rowNumber,
        message: `Fila ${rowNumber}: La fila no tiene las 10 columnas de la plantilla`,
      })
      continue
    }

    const cells = Object.fromEntries(
      COUPON_CSV_HEADERS.map((headerName, cellIndex) => [headerName, row[cellIndex].trim()])
    )
    const nombre = cells.Nombre
    if (nombre) {
      namesByRow.push({ row: rowNumber, nombre })
      candidates.push({ row: rowNumber, nombre })
    }

    const fechaInicioText = stripLeadingTextApostrophe(cells.FechaInicio)
    const fechaFinText = stripLeadingTextApostrophe(cells.FechaFin)
    const fechaInicio = parseCouponCsvDate(fechaInicioText)
    const fechaFin = parseCouponCsvDate(fechaFinText)
    const draftResult = validateAndBuildCouponDraft(
      {
        nombre: cells.Nombre,
        tipoDescuento: cells.Tipo,
        descuentoCompra: cells.DescuentoCompra,
        descuentoEnvio: cells.DescuentoEnvio,
        cantidadDeCanjesPorUsuario: cells.CanjesPorUsuario,
        cantidadDeCanjesTotal: cells.CanjesTotales,
        fechaInicioDate: fechaInicio ? fechaInicio.date : '',
        fechaInicioTime: fechaInicio ? fechaInicio.time : '',
        fechaFinDate: fechaFin ? fechaFin.date : '',
        fechaFinTime: fechaFin ? fechaFin.time : '',
        minimoDeCompra: cells.MinimoCompra,
        sucursal: cells.Sucursal,
      },
      { storeDivisions: options.storeDivisions || [] }
    )

    if (fechaInicioText && !fechaInicio) {
      draftResult.errors.fechaInicio = 'FechaInicio debe tener el formato DD/MM/YYYY HH:MM'
    }
    if (fechaFinText && !fechaFin) {
      draftResult.errors.fechaFin = 'FechaFin debe tener el formato DD/MM/YYYY HH:MM'
    }

    const rowErrors = Object.values(draftResult.errors)
    if (rowErrors.length > 0) {
      invalidRows += 1
      rowErrors.forEach(message => {
        errors.push({ row: rowNumber, message: `Fila ${rowNumber}: ${message}` })
      })
      continue
    }

    validRows += 1
    drafts.push({ ...draftResult.draft, row: rowNumber })
  }

  const duplicates = []
  const grouped = new Map()
  namesByRow.forEach(item => {
    const rowsForName = grouped.get(item.nombre) || []
    rowsForName.push(item.row)
    grouped.set(item.nombre, rowsForName)
  })
  grouped.forEach((rowsForName, nombre) => {
    if (rowsForName.length < 2) return
    duplicates.push({ nombre, rows: rowsForName })
    errors.push({
      row: rowsForName[0],
      message: `El nombre "${nombre}" está duplicado en las filas ${rowsForName.join(', ')}.`,
    })
  })

  const dataRows = validRows + invalidRows
  if (dataRows === 0) {
    errors.push({ row: null, message: 'El archivo no contiene cupones' })
  }
  if (dataRows > CSV_MAX_COUPONS) {
    errors.push({
      row: null,
      message: `El archivo supera el máximo de ${CSV_MAX_COUPONS} cupones`,
    })
  }

  const blocked =
    errors.length > 0 || duplicates.length > 0 || dataRows === 0 || dataRows > CSV_MAX_COUPONS

  return {
    ok: !blocked,
    delimiter,
    dataRows,
    emptyRows,
    validRows,
    invalidRows,
    couponsToCreate: blocked ? 0 : drafts.length,
    errors,
    duplicates,
    existingNames: [],
    candidates,
    drafts: blocked ? [] : drafts,
  }
}

/**
 * Rechaza el lote completo si cualquier nombre ya existe.
 * No escribe y no interpreta una lista vacía de nombres como error de lectura.
 * @param {Object} result
 * @param {string[]} existingNames
 */
export const applyExistingCouponNames = (result, existingNames) => {
  const existing = new Set(existingNames || [])
  const conflicts = (result.candidates || []).filter(item => existing.has(item.nombre))
  if (conflicts.length === 0) {
    return { ...result, existingNames: [] }
  }

  const uniqueNames = [...new Set(conflicts.map(item => item.nombre))]
  return {
    ...result,
    ok: false,
    drafts: [],
    couponsToCreate: 0,
    existingNames: uniqueNames,
    errors: [
      ...result.errors,
      ...conflicts.map(item => ({
        row: item.row,
        message: `Fila ${item.row}: Ya existe un cupón con el nombre "${item.nombre}".`,
      })),
    ],
  }
}

const stripBom = text => (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text)

const headersMatch = (row, headers = COUPON_CSV_HEADERS) => {
  if (!row || row.length !== headers.length) return false
  return headers.every((header, index) => String(row[index]).trim() === header)
}

const isBlankRow = row => row.every(cell => String(cell).trim() === '')

const detectDelimiter = (text, headers = COUPON_CSV_HEADERS) => {
  const headerRecord = firstRecord(text)
  const commaRow = parseCsvRecords(headerRecord, ',').rows[0] || []
  const semicolonRow = parseCsvRecords(headerRecord, ';').rows[0] || []
  const commaMatch = headersMatch(commaRow, headers)
  const semicolonMatch = headersMatch(semicolonRow, headers)
  if (commaMatch && !semicolonMatch) return ','
  if (semicolonMatch && !commaMatch) return ';'

  const commas = countUnquoted(headerRecord, ',')
  const semicolons = countUnquoted(headerRecord, ';')
  if (semicolons > commas) return ';'
  return ','
}

const firstRecord = text => {
  let inQuotes = false
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '"') {
      if (inQuotes && text[index + 1] === '"') {
        index += 1
        continue
      }
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && (char === '\n' || char === '\r')) {
      return text.slice(0, index)
    }
  }
  return text
}

const countUnquoted = (text, delimiter) => {
  let count = 0
  let inQuotes = false
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '"') {
      if (inQuotes && text[index + 1] === '"') {
        index += 1
        continue
      }
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && char === delimiter) count += 1
  }
  return count
}

/**
 * CSV RFC 4180 básico: comillas, comillas escapadas y saltos CRLF/LF.
 * @param {string} text
 * @param {string} delimiter
 */
const parseCsvRecords = (text, delimiter) => {
  const rows = []
  let row = []
  let field = ''
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"'
          index += 1
        } else {
          inQuotes = false
        }
      } else {
        field += char
      }
      continue
    }

    if (char === '"') {
      inQuotes = true
      continue
    }
    if (char === delimiter) {
      row.push(field)
      field = ''
      continue
    }
    if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') index += 1
      row.push(field)
      rows.push(row)
      row = []
      field = ''
      continue
    }
    field += char
  }

  if (inQuotes) {
    return { error: 'El CSV tiene comillas sin cerrar', rows: [] }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }

  return { error: null, rows }
}

export const COUPON_VALIDITY_CSV_HEADERS = ['Nombre', 'FechaInicio', 'FechaFin']

export const COUPON_VALIDITY_CSV_FILENAME = 'vigencias-cupones.csv'

const validityDateMessage = label => `${label} debe tener el formato DD/MM/YYYY HH:MM`

const emptyValidityResult = (overrides = {}) => ({
  ok: false,
  rows: [],
  errors: [],
  duplicates: [],
  dataRows: 0,
  emptyRows: 0,
  modifiedCount: 0,
  unchangedCount: 0,
  errorCount: 0,
  couponsToUpdate: 0,
  ...overrides,
})

const headerHasDuplicates = header => {
  const cells = (header || []).map(cell => String(cell).trim()).filter(Boolean)
  return new Set(cells).size !== cells.length
}

/**
 * Valida el contrato Nombre,FechaInicio,FechaFin.
 * No consulta RTDB ni decide altas. La existencia se resuelve después.
 * @param {string} text
 * @param {{ byteLength?: number }} [options]
 */
export const parseAndValidateCouponValidityCsv = (text, options = {}) => {
  const sourceText = typeof text === 'string' ? text : ''
  const byteLength = options.byteLength ?? new TextEncoder().encode(sourceText).length

  if (byteLength > CSV_MAX_BYTES) {
    return emptyValidityResult({
      errors: [
        {
          row: null,
          message: 'El archivo CSV excede el tamaño máximo permitido (5 MB)',
        },
      ],
    })
  }

  const source = stripBom(sourceText)
  if (!source.trim()) {
    return emptyValidityResult({
      errors: [{ row: null, message: 'El archivo CSV está vacío' }],
    })
  }

  const delimiter = detectDelimiter(source, COUPON_VALIDITY_CSV_HEADERS)
  const parsed = parseCsvRecords(source, delimiter)
  if (parsed.error) {
    return emptyValidityResult({
      delimiter,
      errors: [{ row: null, message: parsed.error }],
    })
  }

  let records = parsed.rows
  if (records.length > 0 && isBlankRow(records[records.length - 1]) && /[\r\n]$/.test(source)) {
    records = records.slice(0, -1)
  }

  const header = records[0]
  if (headerHasDuplicates(header)) {
    return emptyValidityResult({
      delimiter,
      errors: [{ row: 1, message: 'El encabezado contiene columnas duplicadas' }],
    })
  }
  if (!header || !headersMatch(header, COUPON_VALIDITY_CSV_HEADERS)) {
    return emptyValidityResult({
      delimiter,
      errors: [
        {
          row: 1,
          message: `El encabezado no coincide. Se esperaba: ${COUPON_VALIDITY_CSV_HEADERS.join(',')}`,
        },
      ],
    })
  }

  const errors = []
  const rows = []
  let emptyRows = 0

  for (let index = 1; index < records.length; index += 1) {
    const rowNumber = index + 1
    const record = records[index]
    if (isBlankRow(record)) {
      emptyRows += 1
      continue
    }

    const rowErrors = []
    if (record.length !== COUPON_VALIDITY_CSV_HEADERS.length) {
      rowErrors.push(`Fila ${rowNumber}: La fila no tiene las 3 columnas del archivo de vigencias`)
      errors.push({ row: rowNumber, message: rowErrors[0] })
      rows.push({
        row: rowNumber,
        nombre: String(record[0] || '').trim(),
        fechaInicio: null,
        fechaFin: null,
        errors: rowErrors,
        status: 'error',
      })
      continue
    }

    const nombre = String(record[0] || '').trim()
    const inicioText = stripLeadingTextApostrophe(String(record[1] || '').trim())
    const finText = stripLeadingTextApostrophe(String(record[2] || '').trim())

    if (!nombre) {
      rowErrors.push(`Fila ${rowNumber}: Nombre es obligatorio`)
    }

    let fechaInicio = null
    let fechaFin = null
    if (!inicioText) {
      rowErrors.push(`Fila ${rowNumber}: FechaInicio es obligatoria`)
    } else {
      const parsedInicio = parseCouponCsvDate(inicioText)
      if (!parsedInicio) {
        rowErrors.push(`Fila ${rowNumber}: ${validityDateMessage('FechaInicio')}`)
      } else {
        fechaInicio = parsedInicio.timestamp
      }
    }

    if (!finText) {
      rowErrors.push(`Fila ${rowNumber}: FechaFin es obligatoria`)
    } else {
      const parsedFin = parseCouponCsvDate(finText)
      if (!parsedFin) {
        rowErrors.push(`Fila ${rowNumber}: ${validityDateMessage('FechaFin')}`)
      } else {
        fechaFin = parsedFin.timestamp
      }
    }

    if (fechaInicio !== null && fechaFin !== null && fechaFin <= fechaInicio) {
      rowErrors.push(`Fila ${rowNumber}: FechaFin debe ser posterior a FechaInicio`)
    }

    rowErrors.forEach(message => errors.push({ row: rowNumber, message }))
    rows.push({
      row: rowNumber,
      nombre,
      fechaInicio,
      fechaFin,
      errors: rowErrors,
      status: rowErrors.length ? 'error' : 'pending',
    })
  }

  const names = new Map()
  const duplicates = []
  rows.forEach(row => {
    if (!row.nombre || row.errors.some(message => message.includes('Nombre es obligatorio'))) return
    const previous = names.get(row.nombre)
    if (previous) {
      duplicates.push(row.nombre)
      const message = `Fila ${row.row}: Nombre duplicado en el archivo (${row.nombre})`
      if (!row.errors.includes(message)) {
        row.errors.push(message)
        errors.push({ row: row.row, message })
      }
      if (previous.status !== 'error') {
        const firstMessage = `Fila ${previous.row}: Nombre duplicado en el archivo (${row.nombre})`
        previous.errors.push(firstMessage)
        errors.push({ row: previous.row, message: firstMessage })
      }
      row.status = 'error'
      previous.status = 'error'
      return
    }
    names.set(row.nombre, row)
  })

  const dataRows = rows.length
  if (dataRows === 0) {
    return emptyValidityResult({
      delimiter,
      emptyRows,
      errors: [{ row: null, message: 'El archivo CSV no contiene cupones' }],
    })
  }
  if (dataRows > CSV_MAX_COUPONS) {
    return emptyValidityResult({
      delimiter,
      dataRows,
      emptyRows,
      errors: [
        {
          row: null,
          message: `El archivo supera el máximo de ${CSV_MAX_COUPONS} cupones`,
        },
      ],
    })
  }

  const errorCount = rows.filter(row => row.status === 'error').length
  return {
    ok: errorCount === 0 && duplicates.length === 0,
    delimiter,
    rows,
    errors,
    duplicates: [...new Set(duplicates)],
    dataRows,
    emptyRows,
    modifiedCount: 0,
    unchangedCount: 0,
    errorCount,
    couponsToUpdate: 0,
  }
}

const sameTimestamp = (left, right) => Number(left) === Number(right)

/**
 * Compara filas ya parseadas contra las vigencias leídas de RTDB.
 * Un cupón inexistente es error. Fechas iguales no se marcan para escribir.
 * @param {Object} parsed
 * @param {Object<string, { fechaInicio: string|number, fechaFin: string|number }>} catalog
 */
export const classifyCouponValidityUpdates = (parsed, catalog = {}) => {
  if (!parsed || !Array.isArray(parsed.rows) || parsed.rows.length === 0) {
    return {
      ...emptyValidityResult(),
      ...(parsed || {}),
      ok: false,
      couponsToUpdate: 0,
    }
  }

  const errors = [...(parsed.errors || [])]
  const rows = parsed.rows.map(row => {
    if (row.status === 'error') {
      return { ...row, changedFields: [] }
    }

    const current = catalog[row.nombre]
    if (!current) {
      const message = `Fila ${row.row}: El cupón ${row.nombre} no existe`
      errors.push({ row: row.row, message })
      return {
        ...row,
        status: 'error',
        errors: [...row.errors, message],
        changedFields: [],
      }
    }

    const changedFields = []
    if (!sameTimestamp(current.fechaInicio, row.fechaInicio)) changedFields.push('fechaInicio')
    if (!sameTimestamp(current.fechaFin, row.fechaFin)) changedFields.push('fechaFin')

    return {
      ...row,
      originalFechaInicio: current.fechaInicio,
      originalFechaFin: current.fechaFin,
      status: changedFields.length ? 'modified' : 'unchanged',
      changedFields,
    }
  })

  const modifiedCount = rows.filter(row => row.status === 'modified').length
  const unchangedCount = rows.filter(row => row.status === 'unchanged').length
  const errorCount = rows.filter(row => row.status === 'error').length

  return {
    ...parsed,
    rows,
    errors,
    modifiedCount,
    unchangedCount,
    errorCount,
    couponsToUpdate: errorCount === 0 ? modifiedCount : 0,
    ok: errorCount === 0 && !(parsed.errors && parsed.errors.length),
  }
}

export const canConfirmCouponValidityUpdate = result =>
  Boolean(result && result.ok && result.errorCount === 0 && result.couponsToUpdate > 0)

export const formatCouponValidityDate = value => {
  const timestamp = Number(value)
  if (!Number.isFinite(timestamp)) return null
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return null
  const pad = part => String(part).padStart(2, '0')
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const escapeCsvField = value => {
  const text = String(value ?? '')
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

/**
 * Serializa filas cuya identidad ya es la key RTDB.
 * @param {Array<{ nombre: string, fechaInicio: string|number, fechaFin: string|number }>} rows
 */
export const serializeCouponValidityCsv = rows => {
  const lines = [COUPON_VALIDITY_CSV_HEADERS.join(',')]
  for (const row of rows) {
    const inicio = formatCouponValidityDate(row.fechaInicio)
    const fin = formatCouponValidityDate(row.fechaFin)
    if (!row.nombre || !inicio || !fin) {
      return {
        ok: false,
        csv: '',
        error: 'No se puede exportar una vigencia sin nombre o con fecha inválida',
      }
    }
    lines.push(
      [escapeCsvField(row.nombre), escapeCsvField(`'${inicio}`), escapeCsvField(`'${fin}`)].join(
        ','
      )
    )
  }

  return { ok: true, csv: `\uFEFF${lines.join('\n')}`, error: null }
}

/**
 * Exporta la colección cargada completa. No recorta por página ni por filtro visual.
 * @param {Array<Object>} coupons
 */
export const buildCouponValidityCsv = coupons => {
  if (!Array.isArray(coupons)) {
    return { ok: false, csv: '', error: 'No hay cupones cargados para exportar' }
  }
  if (coupons.some(coupon => !coupon || typeof coupon.rtdbKey !== 'string' || !coupon.rtdbKey)) {
    return {
      ok: false,
      csv: '',
      error: 'No se puede exportar porque falta la clave de un cupón',
    }
  }

  return serializeCouponValidityCsv(
    coupons.map(coupon => ({
      nombre: coupon.rtdbKey,
      fechaInicio: coupon.fechaInicio,
      fechaFin: coupon.fechaFin,
    }))
  )
}
